/**
 * NoteDrawer — Add / Edit drawer for campus notes.
 *
 * Standard dp-drawer with a single textarea field.
 *
 * Props:
 *   open      — boolean controlling visibility
 *   onClose   — close callback
 *   onSave    — (noteText) => void
 *   saving    — spinner flag
 *   mode      — 'add' | 'edit'
 *   noteData  — existing note record (used in edit mode)
 */

import { useState, useEffect, useRef } from 'react';
import Drawer from '../../../../../components/v2/Drawer.jsx';

const NoteDrawer = ({ open, onClose, onSave, saving, mode = 'add', noteData }) => {
  const [noteText, setNoteText] = useState('');
  const textareaRef = useRef(null);

  /* Seed form when drawer opens */
  useEffect(() => {
    if (open) {
      setNoteText(mode === 'edit' && noteData?.note ? noteData.note : '');
      setTimeout(() => textareaRef.current?.focus(), 120);
    }
  }, [open, mode, noteData]);

  const isValid = noteText.trim().length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid || saving) return;
    onSave(noteText.trim());
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === 'add' ? 'Add Note' : 'Edit Note'}
      panelAs="aside"
      backdrop={false}
      ariaLabel={mode === 'add' ? 'Add Note' : 'Edit Note'}
      bodyAs="form"
      bodyProps={{ onSubmit: handleSubmit }}
      footer={(
        <>
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={!isValid || saving}>
            {saving ? 'Saving\u2026' : mode === 'add' ? 'Add Note' : 'Save Changes'}
          </button>
        </>
      )}
    >
      <div className="dp-drawer__field">
        <label htmlFor="cpp-note-text" className="dp-drawer__label">Note *</label>
        <textarea
          id="cpp-note-text"
          ref={textareaRef}
          className="dp-drawer__input"
          rows={6}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Enter note text..."
          style={{ resize: 'vertical', minHeight: '125px' }}
        />
      </div>

      {/* Footer */}
    </Drawer>
  );
};

export default NoteDrawer;
